/**
 * Client-side bundle rule + bundle postage discount helpers (mirrors backend/utils/bundlePricing.js).
 * Bundle minItems = distinct products from the same seller (not quantity of one product).
 */

import { computeBuyerProtectionFee } from "./checkoutPricing";

/** Count different products (unique ids), ignoring qty and negotiated offer-bundle lines. */
export function countDistinctBundleProducts(items = []) {
  const ids = new Set();
  let fallbackLines = 0;
  for (const item of items) {
    if (item?.isBundleItemFromOffer) continue;
    const id = String(item?._id || item?.productId || item?.id || "").trim();
    if (id) {
      ids.add(id);
    } else {
      fallbackLines += 1;
    }
  }
  return ids.size + fallbackLines;
}

export function pickApplicableBundleRule(bundleRules, items) {
  const rules = Array.isArray(bundleRules) ? bundleRules : [];
  if (!rules.length) return null;

  const itemsForBundleRules = (items || []).filter((item) => !item?.isBundleItemFromOffer);
  const totalItemsForShop = countDistinctBundleProducts(itemsForBundleRules);
  const subtotalForBundleRules = itemsForBundleRules.reduce(
    (acc, item) => acc + Number(item?.qty || 0) * Number(item?.discountPrice || 0),
    0
  );

  if (totalItemsForShop <= 0 || subtotalForBundleRules <= 0) return null;

  const applicable = rules
    .filter((r) => r.active !== false && Number(r.minItems) <= totalItemsForShop)
    .sort((a, b) => Number(b.minItems) - Number(a.minItems))[0];

  if (!applicable || Number(applicable.discountPercent) <= 0) return null;

  return { applicable, totalItemsForShop, subtotalForBundleRules };
}

export function computeBundleProductDiscount(subtotalForBundleRules, applicable) {
  if (!applicable || !(Number(applicable.discountPercent) > 0)) return 0;
  return Number(
    ((subtotalForBundleRules * Number(applicable.discountPercent)) / 100).toFixed(2)
  );
}

export function sumShopPostage(items) {
  return (items || []).reduce(
    (acc, item) =>
      acc +
      Number(item?.qty || 0) *
        Number(item?.postageFees ?? item?.postageFee ?? item?.shippingFee ?? 0),
    0
  );
}

/** Sum per-unit postage for a list of products (bundle picker, offers). */
export function sumProductsPostage(products) {
  return (products || []).reduce(
    (acc, p) =>
      acc + Number(p?.postageFees ?? p?.postageFee ?? p?.shippingFee ?? 0),
    0
  );
}

export function estimateBundlePostageSavings(products, qualifiesForBundle, bundlePostageConfig) {
  if (!qualifiesForBundle) return 0;
  return computeBundlePostageDiscountAmount(
    sumProductsPostage(products),
    bundlePostageConfig
  );
}

export function computeBundlePostageDiscountAmount(postageTotal, bundlePostageConfig) {
  if (!(Number(postageTotal) > 0)) return 0;
  const cfg = bundlePostageConfig || {};
  if (cfg.enabled === false) return 0;
  const pct = Number(cfg.percentage ?? 30);
  if (!(pct > 0)) return 0;
  return Number(((Number(postageTotal) * Math.min(100, pct)) / 100).toFixed(2));
}

/** Aggregate bundle product + postage discounts across shops in cart. */
export function computeCartBundleTotals(cart, shopBundleRulesById, bundlePostageConfig) {
  let bundleProductDiscount = 0;
  let bundlePostageDiscount = 0;
  const appliedShopIds = [];

  const uniqueShopIds = Array.from(new Set((cart || []).map((i) => i.shopId).filter(Boolean)));

  uniqueShopIds.forEach((shopId) => {
    const rules = shopBundleRulesById[String(shopId)] || [];
    const itemsForShop = (cart || []).filter((item) => String(item.shopId) === String(shopId));
    const resolved = pickApplicableBundleRule(rules, itemsForShop);
    if (!resolved) return;

    appliedShopIds.push(String(shopId));
    bundleProductDiscount += computeBundleProductDiscount(
      resolved.subtotalForBundleRules,
      resolved.applicable
    );

    const shopPostage = sumShopPostage(
      itemsForShop.filter((item) => !item.isBundleItemFromOffer)
    );
    bundlePostageDiscount += computeBundlePostageDiscountAmount(
      shopPostage,
      bundlePostageConfig
    );
  });

  return {
    bundleProductDiscount: Number(bundleProductDiscount.toFixed(2)),
    bundlePostageDiscount: Number(bundlePostageDiscount.toFixed(2)),
    appliedShopIds,
  };
}

export const defaultBundlePostageConfig = { enabled: true, percentage: 30 };

/** Pricing preview for bundle picker / review (qty 1 per selected product). */
export function computeBundleSelectionPricing({
  products = [],
  bundleRules = [],
  bundlePostageConfig = defaultBundlePostageConfig,
  buyerProtectionConfig = { enabled: true, fixedFee: 0.7, percentage: 2 },
}) {
  const selectedProducts = Array.isArray(products) ? products.filter(Boolean) : [];
  const subtotal = selectedProducts.reduce(
    (sum, product) => sum + Number(product?.discountPrice || 0),
    0
  );

  const activeRules = (Array.isArray(bundleRules) ? bundleRules : [])
    .filter((rule) => rule?.active !== false)
    .sort((a, b) => Number(a?.minItems || 0) - Number(b?.minItems || 0));

  const appliedRule = activeRules
    .filter((rule) => Number(rule?.minItems || 0) <= selectedProducts.length)
    .sort((a, b) => Number(b?.minItems || 0) - Number(a?.minItems || 0))[0];

  const discountPercent = Number(appliedRule?.discountPercent || 0);
  const bundleDiscount =
    subtotal > 0 && discountPercent > 0
      ? Number(((subtotal * discountPercent) / 100).toFixed(2))
      : 0;

  const qualifiesForBundle = Boolean(appliedRule);
  const postageTotal = sumProductsPostage(selectedProducts);
  const postageDiscount = estimateBundlePostageSavings(
    selectedProducts,
    qualifiesForBundle,
    bundlePostageConfig
  );
  const postageCharged = Math.max(0, Number((postageTotal - postageDiscount).toFixed(2)));
  const buyerProtectionFee = computeBuyerProtectionFee(subtotal, buyerProtectionConfig);
  const total = Math.max(
    0,
    Number((subtotal + postageCharged + buyerProtectionFee - bundleDiscount).toFixed(2))
  );

  return {
    itemCount: selectedProducts.length,
    subtotal,
    bundleDiscount,
    discountPercent,
    postageTotal,
    postageDiscount,
    postageCharged,
    buyerProtectionFee,
    total,
    qualifiesForBundle,
    appliedRule,
  };
}
